import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
// Theme & Context
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
// Hooks
import { useToast, ToastProvider } from './hooks/useToast';
// Components
import ScrollToTop from './components/ScrollToTop';
import BackToTopButton from './components/BackToTopButton';
import CookieConsent from './components/CookieConsent';
import Toast from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
// Layouts
import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';
import PlatformLayout from './layouts/PlatformLayout';
// Public Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Features from './pages/Features';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Values from './pages/Values';
import Vision from './pages/Vision';
// Company Pages
import Careers from './pages/Careers';
// Product Pages
import Pricing from './pages/Pricing';
import Security from './pages/Security';
import Support from './pages/Support';
import Demo from './pages/Demo';
import Appointment from './pages/Appointment';
// Auth Pages
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import PasswordReset from './pages/PasswordReset';
import VerifyOtp from './pages/VerifyOtp';
import ResetPasswordConfirm from './pages/ResetPasswordConfirm';
// Dashboard Pages
import { ClientDashboard, PayrollForm, UserManagement } from './pages/dashboard/DashboardPages';
import OrganisationProfile from './pages/dashboard/admin/OrganisationProfile';
// Legal Pages
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Gdpr from './pages/Gdpr';
import Cookies from './pages/Cookies';
// Styles
import './styles/globals.css';
import './styles/animations.css';
import './styles/theme.css';
import './styles/DarkMode.css';
import './styles/Toast.css';
import Employees from './pages/dashboard/Employees';
import Payslips from './pages/dashboard/Payslips';
import EmailSmsSettings from './pages/dashboard/admin/EmailSmsSettings';
import Billing from './pages/dashboard/admin/Billing';
import AuditLog from './pages/dashboard/admin/AuditLog';
import Compliance from './pages/dashboard/admin/Compliance';
import Leave from './pages/dashboard/Leave';
import Recruitment from './pages/dashboard/Recruitment';
import OnboardingTemplates from './pages/dashboard/OnboardingTemplates';
import EmployeeOnboarding from './pages/dashboard/EmployeeOnboarding';
import Invoicing from './pages/dashboard/Invoicing';
import AP from './pages/dashboard/AP';
import Banking from './pages/dashboard/Banking';
import Accounting from './pages/dashboard/Accounting';
import Vat from './pages/dashboard/Vat';
import Appointments from './pages/dashboard/Appointments';
import Documents from './pages/dashboard/Documents';
import Workflow from './pages/dashboard/Workflow';
import PlatformOrganisations from './pages/platform/Organisations';
import PlatformSubscriptionPlans from './pages/platform/SubscriptionPlans';
import PlatformPendingPayments from './pages/platform/PendingPayments';
import PlatformWebhookEvents from './pages/platform/WebhookEvents';
import PlatformStaff from './pages/platform/Staff';
import Reports from './pages/dashboard/Reports';
import DepartmentsJobTitles from './pages/dashboard/DepartmentsJobTitles';
import PayrollSettings from './pages/dashboard/PayrollSettings';
import Marketing from './pages/dashboard/Marketing';
import Notifications from './pages/dashboard/Notifications';
import IntegrationLogs from './pages/dashboard/IntegrationLogs';
import PlatformEmailSettings from './pages/platform/EmailSettings';
import FeatureBlockedBanner from './components/FeatureBlockedBanner';

function AppContent() {
  const { toasts, removeToast } = useToast();
  return (
    <>
      <Toast toasts={toasts} onRemove={removeToast} />
      <Router>
        <ScrollToTop />
        <BackToTopButton />
        <CookieConsent />
        <FeatureBlockedBanner />
        <AnimatePresence mode="wait">
          <Routes>
            <Route element={<MainLayout />}>
              {/* Main */}
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="services" element={<Services />} />
              <Route path="features" element={<Features />} />
              <Route path="blog" element={<Blog />} />
              <Route path="contact" element={<Contact />} />
              <Route path="values" element={<Values />} />
              <Route path="vision" element={<Vision />} />
              {/* Company */}
              <Route path="careers" element={<Careers />} />
              {/* Product */}
              <Route path="pricing" element={<Pricing />} />
              <Route path="security" element={<Security />} />
              <Route path="support" element={<Support />} />
              <Route path="demo" element={<Demo />} />
              <Route path="book" element={<Appointment />} />
              {/* Legal */}
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
              <Route path="gdpr" element={<Gdpr />} />
              <Route path="cookies" element={<Cookies />} />
            </Route>

            {/* Auth — standalone, no marketing-site chrome (header/footer) */}
            <Route path="signin" element={<SignIn />} />
            <Route path="signup" element={<SignUp />} />
            <Route path="password-reset" element={<PasswordReset />} />
            <Route path="verify-otp" element={<VerifyOtp />} />
            <Route path="reset-password/:token" element={<ResetPasswordConfirm />} />

            {/* Authenticated app — customer/tenant side */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ClientDashboard />} />
              <Route path="payroll/new" element={<PayrollForm />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="admin/organisation" element={<OrganisationProfile />} />
              <Route path="employees" element={<Employees />} />
              <Route path="employees/:employeeId/payslips" element={<Payslips />} />
              <Route path="admin/email-sms" element={<EmailSmsSettings />} />
              <Route path="admin/billing" element={<Billing />} />
              <Route path="admin/audit-log" element={<AuditLog />} />
              <Route path="admin/compliance" element={<Compliance />} />
              <Route path="leave" element={<Leave />} />
              <Route path="recruitment" element={<Recruitment />} />
              <Route path="onboarding-templates" element={<OnboardingTemplates />} />
              <Route path="employees/:employeeId/onboarding" element={<EmployeeOnboarding />} />
              <Route path="invoicing" element={<Invoicing />} />
              <Route path="ap" element={<AP />} />
              <Route path="banking" element={<Banking />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="vat" element={<Vat />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="documents" element={<Documents />} />
              <Route path="workflow" element={<Workflow />} />
              <Route path="reports" element={<Reports />} />
              <Route path="departments" element={<DepartmentsJobTitles />} />
              <Route path="payroll-settings" element={<PayrollSettings />} />
              <Route path="marketing" element={<Marketing />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="integration-logs" element={<IntegrationLogs />} />
            </Route>

            {/* Authenticated app — platform-admin side (separate tier, own layout, no tenant scope) */}
            <Route
              path="platform"
              element={
                <ProtectedRoute>
                  <PlatformLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="organisations" replace />} />
              <Route path="organisations" element={<PlatformOrganisations />} />
              <Route path="subscription-plans" element={<PlatformSubscriptionPlans />} />
              <Route path="pending-payments" element={<PlatformPendingPayments />} />
              <Route path="webhook-events" element={<PlatformWebhookEvents />} />
              <Route path="staff" element={<PlatformStaff />} />
              <Route path="email-settings" element={<PlatformEmailSettings />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </Router>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;